/** UI labels for backend admission enums. Mapping only — no local rule engine. */

export const ADMISSION_READINESS = {
  NOT_EVALUATED: 'NOT_EVALUATED',
  EVALUATING: 'EVALUATING',
  READY_TO_CERTIFY: 'READY_TO_CERTIFY',
  REVIEW_REQUIRED: 'REVIEW_REQUIRED',
  BLOCKED: 'BLOCKED',
  CERTIFIED: 'CERTIFIED',
  RETURNED: 'RETURNED',
};

export const ADMISSION_CHECK = {
  READY: 'READY',
  LINKED: 'LINKED',
  REVIEW_NEEDED: 'REVIEW_NEEDED',
  BLOCKED: 'BLOCKED',
};

const READINESS_LABELS = {
  NOT_EVALUATED: 'Not evaluated',
  EVALUATING: 'Processing admission checks…',
  READY_TO_CERTIFY: 'Ready for Provider review',
  REVIEW_REQUIRED: 'Review required',
  BLOCKED: 'Blocked',
  CERTIFIED: 'Certified — ready for Provider review',
  RETURNED: 'Returned',
};

const READINESS_TONES = {
  NOT_EVALUATED: 'neutral',
  EVALUATING: 'info',
  READY_TO_CERTIFY: 'success',
  REVIEW_REQUIRED: 'warning',
  BLOCKED: 'danger',
  CERTIFIED: 'success',
  RETURNED: 'warning',
};

const CHECK_LABELS = {
  READY: 'Ready',
  LINKED: 'Linked',
  REVIEW_NEEDED: 'Review needed',
  BLOCKED: 'Blocked',
};

const CHECK_TONES = {
  READY: 'success',
  LINKED: 'info',
  REVIEW_NEEDED: 'warning',
  BLOCKED: 'danger',
};

const VAULT_ROLE_LABELS = {
  AUTHORITATIVE_CANDIDATE: 'Authoritative candidate',
  REPOSITORY_PRIMARY: 'Primary record',
  SUPPORTING: 'Supporting',
  AMENDMENT: 'Amendment',
  SECURITY_INSTRUMENT: 'Security instrument',
  NEEDS_REVIEW: 'Needs review',
};

const TREATMENT_LABELS = {
  AUTHORITATIVE_CANDIDATE: 'Authoritative candidate',
  REPOSITORY_ONLY: 'Repository only',
  MIXED: 'Mixed',
};

const SOURCE_FORM_LABELS = {
  BORN_DIGITAL: 'Born digital',
  SCANNED_PAPER: 'Scanned paper',
  MIXED: 'Mixed',
  UNKNOWN: 'Unknown',
};

const INSTRUMENT_LABELS = {
  ARTICLE_3_NOTE: 'Article 3 note',
  RETAIL_INSTALLMENT_CONTRACT: 'Retail installment contract',
  LEASE: 'Lease',
  SECURITY_AGREEMENT: 'Security agreement',
  MORTGAGE: 'Mortgage',
  DOCUMENT_OF_TITLE: 'Document of title',
  OTHER_AGREEMENT: 'Other agreement',
};

const CLASSIFICATION_SOURCE_LABELS = {
  PROFILE: 'Programme profile',
  RULE: 'Admission rule',
  AI: 'System proposal',
  USER_CONFIRMATION: 'Originator confirmation',
  PROVIDER_OVERRIDE: 'Provider override',
};

const PAPER_ORIGINAL_LABELS = {
  NOT_APPLICABLE: 'Not applicable',
  NONE: 'None',
  CONTINUING: 'Continuing',
  UNRESOLVED: 'Unresolved',
};

const RULE_OUTCOME_LABELS = {
  PASS: 'Pass',
  FAIL: 'Fail',
  WARN: 'Warning',
  SKIP: 'Skipped',
};

export function labelFor(map, value, fallback = '—') {
  if (value === undefined || value === null || value === '') return fallback;
  return map[value] || String(value);
}

export function admissionReadinessLabel(status) {
  return labelFor(READINESS_LABELS, status);
}

export function admissionReadinessTone(status) {
  return READINESS_TONES[status] || 'neutral';
}

export function admissionCheckLabel(check) {
  return labelFor(CHECK_LABELS, check);
}

export function admissionCheckTone(check) {
  return CHECK_TONES[check] || 'neutral';
}

export function vaultRoleLabel(role) {
  return labelFor(VAULT_ROLE_LABELS, role);
}

export function intendedTreatmentLabel(value) {
  return labelFor(TREATMENT_LABELS, value);
}

export function sourceFormLabel(value) {
  return labelFor(SOURCE_FORM_LABELS, value);
}

export function instrumentTypeLabel(value) {
  return labelFor(INSTRUMENT_LABELS, value);
}

export function classificationSourceLabel(value) {
  return labelFor(CLASSIFICATION_SOURCE_LABELS, value);
}

export function paperOriginalLabel(value) {
  return labelFor(PAPER_ORIGINAL_LABELS, value);
}

export function ruleOutcomeLabel(value) {
  return labelFor(RULE_OUTCOME_LABELS, value);
}

export function legalRegimeLabel(value) {
  if (value === undefined || value === null || value === '') return '—';
  return String(value);
}

export function formatLegalRegimes(regimes) {
  if (!Array.isArray(regimes) || !regimes.length) return '—';
  return regimes.map(legalRegimeLabel).join(', ');
}

export const CERTIFICATION_CONSEQUENCE =
  'Accept each mandatory statement identifier from this admission snapshot. Certification submits the package for Provider review. Authority stays pending, and this does not admit a document into the eVault.';
